import { Formik } from "formik";
import { createRef, FC, useEffect, useState } from "react";
import { Button, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { getSiteByTCCode } from "../../../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../../../store/slices/town-council.slice";
import { getBlocksBySiteCode } from "../../../../../store/slices/location-block.slice";
import { AssetFilterValues } from "../../../../../core/enums/asset-enums";
import { B2Button, B2Form, B2Select, B2Row, B2Input } from "@hdb/biome-2";
import FormBuilderComponent, {
  FormConfig,
} from "src/shared/form-builder/form-builder.component";
import { setAssetMainSearchFilter } from "src/store/slices/asset.slice";
import { getLevelsByBlockCode } from "src/core/api/services/dashbaorad-details/location-block.services";
import { useQuery } from "@tanstack/react-query";

interface FilterFormValues {
  tcCode: string;
  siteCode: string;
  blockCode: string;
  levelCode: string;
}

interface AssetMainFilterComponentProps {
  getSelectedFilterInfo: (filterInfo: AssetFilterValues) => any;
  assetsByBlockResponse?: any[];
  onLevelOptionsChange?: (levels: Array<{ label: string; value: string }>) => void;
}

const AssetMainFilterComponent: FC<AssetMainFilterComponentProps> = ({
  getSelectedFilterInfo,
  assetsByBlockResponse,
  onLevelOptionsChange,
}) => {
  const dispatch = useDispatch();
  const formRef = createRef();

  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [levelOptions, setLevelOptions] = useState<any>([]);
  const [filterUpdateCount, setFilterUpdateCount] = useState(0);
  const [formConfiguration, setFormConfiguration] = useState({} as any);

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    level: "",
  });

  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  const {
    data: levelsByBlockCodeResponse,
    isLoading: isLoadingLevelsByBlockCode,
  } = useQuery({
    queryKey: ["getLevelsByBlockCode", formFilterInitialValues.block],
    queryFn: ({ signal }) =>
      getLevelsByBlockCode(formFilterInitialValues.block, signal),
    enabled: Boolean(formFilterInitialValues.block),
  });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
      dispatch(
        setAssetMainSearchFilter({
          townCouncil: currentTC.value,
          site: null,
          block: null,
        }),
      );
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) {
      setBlockOptions([]);
      return;
    }

    setSiteOptions(sites);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [blockDataSiteCode]);

  useEffect(() => {
    if (levelsByBlockCodeResponse?.length) {
      const levels = levelsByBlockCodeResponse.map((level: any) => ({
        label: level?.displayLevel,
        value: level?.displayLevel,
      }));

      setLevelOptions(levels);
      setFilterUpdateCount(filterUpdateCount + 1);

      if (onLevelOptionsChange) {
        onLevelOptionsChange(levels);
      }
    }
  }, [levelsByBlockCodeResponse, onLevelOptionsChange]);

  // useEffect(() => {
  //   if (assetsByBlockResponse) {
  //     const uniqueLevels = assetsByBlockResponse
  //       .map((asset: any) => asset?.floorCode)
  //       .filter(Boolean)
  //       .filter((code: string, index: number, self: string[]) => index === self.indexOf(code));
  //     const levelOpts = uniqueLevels.map((code: string) => ({
  //       label: code,
  //       value: code,
  //     }));
  //     setLevelOptions(levelOpts);
  //     setFilterUpdateCount(filterUpdateCount + 1);
  //   } else {
  //     setLevelOptions([]);
  //   }
  // }, [assetsByBlockResponse]);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);
    setLevelOptions([]);

    setFormFilterInitialValues({
      ...formFilterInitialValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
      level: "",
    });

    if (!selectedTC) return;

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));

    dispatch(
      setAssetMainSearchFilter({
        townCouncil: selectedTC,
        site: null,
        block: null,
        level: null,
      }),
    );
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);
    setLevelOptions([]);

    setFormFilterInitialValues({
      ...formFilterInitialValues,
      site: selectedSite,
      block: "",
    });

    if (!selectedSite) return;

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));

    dispatch(
      setAssetMainSearchFilter({
        townCouncil: formFilterInitialValues?.townCouncil,
        site: selectedSite,
        block: null,
      }),
    );
  };

  const onChangeBlock = (selectedBlock: any) => {
    setLevelOptions([]);

    setFormFilterInitialValues({
      ...formFilterInitialValues,
      block: selectedBlock,
      level: "",
    });

    dispatch(
      setAssetMainSearchFilter({
        townCouncil: formFilterInitialValues?.townCouncil,
        site: formFilterInitialValues?.site,
        block: selectedBlock,
        level: null,
      }),
    );
  };

  const onChangeLevel = (selectedLevel: any) => {
    setFormFilterInitialValues({
      ...formFilterInitialValues,
      level: selectedLevel,
    });

    dispatch(
      setAssetMainSearchFilter({
        townCouncil: formFilterInitialValues?.townCouncil,
        site: formFilterInitialValues?.site,
        block: formFilterInitialValues?.block,
        level: selectedLevel,
      }),
    );
  };

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    if (form) {
      const { townCouncil, site, block, level } = form.values;
      form.handleSubmit();

      if (!townCouncil || !site) return;

      const selectedTc =
        townCouncil && townCouncilData?.data?.length
          ? townCouncilData.data.find((tc: any) => tc.tcCode === townCouncil)
          : "";

      const selectedSite =
        site && siteDataByTCCode?.data?.length
          ? siteDataByTCCode.data.find((s: any) => s.siteCode === site)
          : "";

      const selectedBlock =
        block && blockDataSiteCode?.data?.length
          ? blockDataSiteCode.data.find((b: any) => b.blockCode === block)
          : "";

      const selectedLevel = level
        ? { levelCode: level, levelName: level }
        : { levelCode: null, levelName: null };

      getSelectedFilterInfo({
        tc: { tcCode: selectedTc.tcCode, tcName: selectedTc.name },
        block: {
          blockCode: selectedBlock.blockCode,
          blockName: selectedBlock.blockName,
        },
        site: { siteCode: selectedSite.siteCode, siteName: selectedSite.name },
        level: selectedLevel,
      });
    }
  };

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setFilterUpdateCount(filterUpdateCount + 1);

    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      level: "",
    });

    dispatch(
      setAssetMainSearchFilter({ townCouncil: null, site: null, block: null, level: null }),
    );

    setSiteOptions([]);
    setBlockOptions([]);
    setLevelOptions([]);

    getSelectedFilterInfo({
      tc: { tcCode: "", tcName: "" },
      block: { blockCode: "", blockName: "" },
      site: { siteCode: "", siteName: "" },
      level: { levelCode: null, levelName: null },
    });
  };

  useEffect(() => {
    const formConfiguration = {
      controls: [
        {
          name: "townCouncil",
          label: "Town Council",
          placeholder: "Select Town Council",
          type: "searchable-select",
          options: tcOptions,
          isLoading: currentTC.value.length
            ? false
            : Boolean(isLoadingTownCouncils),
          onChange: onChangeTC,
          required: true,
          disabled: false,
        },
        {
          name: "site",
          label: "Select Site",
          placeholder: "Select Site",
          type: "searchable-select",
          options: siteOptions,
          onChange: onChangeSite,
          required: true,
          isLoading: isLocationSitesByTCCode,
        },
        {
          name: "block",
          label: "Select Block",
          placeholder: "Select Block",
          type: "searchable-select",
          onChange: onChangeBlock,
          options: blockOptions,
          required: false,
          isLoading: isLoadingBlocksBySiteCode,
        },
        {
          name: "level",
          label: "Select Level",
          placeholder: "Select Level",
          type: "searchable-select",
          onChange: onChangeLevel,
          options: levelOptions,
          required: false,
          isLoading: isLoadingLevelsByBlockCode,
        },
      ],
    };

    setFormConfiguration(formConfiguration);
  }, [dispatch, tcOptions, siteOptions, blockOptions, levelOptions]);

  const validationSchema = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
    site: yup.string().required("Site is required"),
  });

  useEffect(() => {
    // Cleanup function runs when component unmounts
    return () => {
      dispatch(
        setAssetMainSearchFilter({
          townCouncil: null,
          site: null,
          block: null,
          level: null,
        }),
      );
    };
  }, []);

  return (
    <div className="p-3 asset-main-filter-col">
      <div className="bg-card shadow-sm asset-main-filter-card">
        <FormBuilderComponent
          renderCount={filterUpdateCount}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          validation={validationSchema}
          formConfig={formConfiguration}
        />
        <div className="asset-main-filter-actions">
          <Button variant="default" onClick={() => onReset()}>
            Reset
          </Button>
          <Button variant="primary" onClick={() => onSubmitFilter()}>
            Filter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssetMainFilterComponent;
